import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  useCallback,
  useState,
} from "react";
import {
  CircularProgress,
  Alert,
  Box,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { isEqual } from "lodash";

// Create context for shared state management
const SharedContext = createContext(null);

// Default action types for state management
const defaultActionTypes = {
  SET_LOADING: "SET_LOADING",
  UPDATE_DATA: "UPDATE_DATA",
  RESET_DATA: "RESET_DATA",
  SET_ERROR: "SET_ERROR",
};

// Function to create custom action types by merging with defaults
const createActionTypes = (customActionTypes = {}) => {
  return { ...customActionTypes, ...defaultActionTypes };
};

// Default action handlers for common state actions
const createDefaultActionHandlers = (initialValues) => ({
  [defaultActionTypes.SET_LOADING]: (state, action) => ({
    ...state,
    loading: action.payload,
  }),
  [defaultActionTypes.UPDATE_DATA]: (state, action) => ({
    ...state,
    data: { ...action.payload },
  }),
  [defaultActionTypes.RESET_DATA]: () => initialValues,
  [defaultActionTypes.SET_ERROR]: (state, action) => ({
    ...state,
    error: action.payload,
  }),
});

// Function to create action handlers by merging with default ones
const createActionHandlers = (initialValues, customActionHandlers = {}) => ({
  ...customActionHandlers,
  ...createDefaultActionHandlers(initialValues),
});

// Provider component for managing shared state across the application
export const SharedProvider = ({
  children,
  customInitialValues = {},
  storageKey = "sharedState",
  customActionHandlers = {},
  customActionTypes = {},
  sharedActionsFunctionMore = {},
}) => {
  const theme = useTheme();
  const errorColor = theme.palette.error.main;

  if (!storageKey || typeof storageKey !== "string") {
    throw new Error("storageKey must be a non-empty string");
  }

  const [namespacedKey, initialValues] = useMemo(
    () => [
      `myApp_${storageKey}`,
      {
        ...customInitialValues,
        loading: false,
        data: {},
        error: null,
      },
    ],
    [storageKey, customInitialValues]
  );

  // Initialize state with stored data from localStorage if available
  const [storedData, setStoredData] = useState(() => {
    try {
      if (typeof window === "undefined" || !window.localStorage) {
        console.warn("localStorage is not available");
        return { data: initialValues?.data || {} };
      }

      const item = localStorage.getItem(namespacedKey);

      return item
        ? JSON.parse(item)
        : structuredClone({ data: initialValues?.data || {} });
    } catch (error) {
      console.error(
        "Error accessing localStorage during initialization:",
        error
      );
      return structuredClone({ data: initialValues?.data || {} });
    }
  });

  // Function to update localStorage with the new state data
  const setLocalStorageData = useCallback(
    (newObject) => {
      setStoredData((prev) => {
        try {
          const updatedObject =
            typeof newObject === "object" && newObject !== null
              ? { ...newObject }
              : {};

          if (!isSerializable(updatedObject)) {
            throw new Error("Data is not serializable");
          }

          if (JSON.stringify(prev) === JSON.stringify(updatedObject)) {
            return prev;
          }

          localStorage.setItem(namespacedKey, JSON.stringify(updatedObject));
          return updatedObject;
        } catch (error) {
          console.error("Error saving to localStorage:", error);
          return prev;
        }
      });
    },
    [namespacedKey, initialValues?.data]
  );

  // Sync state with localStorage when it changes across tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === namespacedKey) {
        try {
          const newValue = event.newValue
            ? JSON.parse(event.newValue)
            : structuredClone({ data: initialValues?.data || {} });
          setStoredData(newValue);
        } catch (error) {
          console.error("Error parsing localStorage data from event:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [namespacedKey, initialValues?.data]);

  const ActionTypes = useMemo(
    () => createActionTypes(customActionTypes),
    [customActionTypes]
  );

  const ActionHandlers = useMemo(
    () => createActionHandlers(initialValues, customActionHandlers),
    [initialValues, customActionHandlers]
  );

  // Reducer function to handle state updates based on actions
  const reducer = (state, action) => {
    const handler = ActionHandlers[action?.type];
    if (!handler) {
      throw new Error(`Unknown action type: ${action?.type}`);
    }
    return handler(state, action);
  };

  // Middleware to apply additional logic after state updates
  const middlewareReducer = (state, action) => {
    try {
      const newState = reducer(state, action);

      if (!isEqual(storedData?.data, newState?.data)) {
        setLocalStorageData({ data: newState?.data || {} });
      }

      return newState;
    } catch (error) {
      console.error("Error in middlewareReducer:", error);
      return state;
    }
  };

  const [state, dispatch] = useReducer(
    middlewareReducer,
    { ...initialValues, ...storedData },
    (stored) => stored ?? initialValues
  );

  const memoizedDispatch = useCallback((action) => dispatch(action), []);

  // Action dispatchers for loading, error, and data updates
  const setLoading = (isLoading) =>
    memoizedDispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });

  const setError = (error) =>
    memoizedDispatch({ type: ActionTypes.SET_ERROR, payload: error });

  const handleData = (type, payload) => {
    setLoading(true);
    setError(null);
    try {
      memoizedDispatch({ type, payload });
    } catch (error) {
      setError(`Error in handleData: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle global errors
  useEffect(() => {
    const handleGlobalError = (error) => {
      console.error("Unhandled error in SharedProvider:", error);
      setError(`An unexpected error occurred: ${JSON.stringify(error)}`);
    };

    window.addEventListener("error", handleGlobalError);
    window.addEventListener("unhandledrejection", (event) =>
      handleGlobalError(event.reason)
    );

    return () => {
      window.removeEventListener("error", handleGlobalError);
      window.removeEventListener("unhandledrejection", handleGlobalError);
    };
  }, []);

  const sharedActionsFunction = {
    ...sharedActionsFunctionMore,
    updateData: (payload) => handleData(ActionTypes.UPDATE_DATA, payload),
    resetData: () => handleData(ActionTypes.RESET_DATA, null),
  };

  const contextValue = useMemo(
    () => ({
      ...sharedActionsFunction,
      state: { ...state, ...storedData },
      dispatch: memoizedDispatch,
    }),
    [state, storedData, memoizedDispatch, setLocalStorageData]
  );

  return (
    <SharedContext.Provider value={contextValue}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          backgroundColor: "transparent",
        }}
      >
        {storedData?.loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(128, 128, 128, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress />
            <IconButton
              onClick={() => setLoading(false)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "white",
                backgroundColor: errorColor,
                "&:hover": { backgroundColor: alpha(errorColor, 0.7) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        )}
        {storedData?.error && (
          <Box
            sx={{
              position: "fixed",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              width: "90%",
              maxWidth: 400,
            }}
          >
            <Alert severity="error" onClose={() => setError(null)}>
              {storedData.error}
            </Alert>
          </Box>
        )}
        {!storedData?.loading && !storedData?.error && children}
      </Box>
    </SharedContext.Provider>
  );
};

// Hook to access shared context values
export const useSharedContext = () => {
  const context = useContext(SharedContext);
  if (!context) {
    throw new Error("useSharedContext must be used within a SharedProvider");
  }
  return context;
};

export const isSerializable = (data) => {
  try {
    JSON.stringify(data);
    return true;
  } catch {
    return false;
  }
};
